<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
      </div>

      <v-card-text class="editor-content">
        <section class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8"><v-text-field v-model="fields.title" label="Name" hide-details/></v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </section>

        <SectionSettingsContainerFuild
          class="mb-4"
          :settings="fields.settings"
          :container-page="fields.containerPage"
          :use-background-mobile="true"
          :show-container-page="false"
        />

        <section class="mb-4">
          <label class="label-input">Header</label>
          <div class="group-item">
            <p class="field-label">Title</p>
            <ckeditor :editor="editor" v-model="fields.header.title" :config="editorConfig" />
            <p class="field-label mt-4">Description</p>
            <ckeditor :editor="editor" v-model="fields.header.description" :config="editorConfig" />
            <p class="field-label mt-4">Decoration</p>
            <UploadImage
              :image="{ imageSrc: fields.header.decor }"
              height="100"
              type-upload="image"
              @callback="handleUploadDecor"
              @callbackDelete="handleRemoveDecor"
            />
          </div>
        </section>

        <section class="mb-4">
          <label class="label-input">Media</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <v-checkbox v-model="fields.media.show" label="Show media" hide-details />
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="fields.media.type" :items="mediaTypeOptions" label="Media type" hide-details />
              </v-col>
            </v-row>

            <template v-if="fields.media.type === 'video'">
              <v-text-field class="mt-4" v-model="fields.media.videoSrc" label="Video URL" hide-details />
              <video
                v-if="fields.media.videoSrc"
                class="media-preview"
                :src="fields.media.videoSrc"
                controls
                muted
                playsinline
              />
            </template>
            <template v-else>
              <p class="field-label mt-4">Image</p>
              <UploadImage
                :image="{ imageSrc: fields.media.imageSrc }"
                height="220"
                type-upload="image"
                @callback="handleUploadMediaImage"
                @callbackDelete="handleRemoveMediaImage"
              />
            </template>

            <p class="field-label mt-4">Subtext</p>
            <v-text-field v-model="fields.media.subtext" label="Video URL" hide-details />
          </div>
        </section>

        <section class="mb-4">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Features</label>
            <v-btn small text color="primary" @click="handleAddFeature">
              <v-icon small>mdi-plus</v-icon>Add feature
            </v-btn>
          </div>
          <v-expansion-panels accordion>
            <v-expansion-panel v-for="(item, index) in fields.features" :key="`furnitureshop-feature-${index}`">
              <v-expansion-panel-header>
                <span>{{ item.text || 'Feature ' + (index + 1) }}</span>
                <template #actions>
                  <v-btn icon @click.stop="handleRemoveFeature(index)">
                    <v-icon color="error">mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="field-label">Icon</p>
                <UploadImage
                  :image="{ imageSrc: item.icon }"
                  height="100"
                  type-upload="image"
                  @callback="obj => handleUploadFeatureIcon(obj, index)"
                  @callbackDelete="() => handleRemoveFeatureIcon(index)"
                />
                <v-text-field v-model="item.text" class="mt-4" label="Text" hide-details />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </section>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions>
          <v-spacer />
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const featureIcon = 'https://cdn.skyfastimg.com/images/2026/08/24/image-6a8bc86498460bd495d50d46.png?w=200';
const defaultFeature = text => ({ icon: featureIcon, text });

export default {
  name: 'FurnitureshopBanner03',
  components: { UploadImage, SectionSettingsContainerFuild },
  props: { sectionEditor: { type: Object, required: true } },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      isUploadingVideo: false,
      mediaTypeOptions: [
        { text: 'Video', value: 'video' },
        { text: 'Image', value: 'image' },
      ],
      fields: {
        title: 'Furnitureshop banner 03',
        enableAnchor: false,
        settings: {
          paddingTop: false,
          paddingBottom: false,
          backgroundColor: '#05090D',
          textColor: '#FFFFFF',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
          useBackgroundMobile: true,
          backgroundImageMobile: '',
        },
        containerPage: { enableStyle: false, background: '#FFFFFF' },
        header: { title: '', description: '', decor: '' },
        media: { show: true, type: 'video', videoSrc: '', imageSrc: '', subtext: '' },
        features: [defaultFeature('UV Protection'), defaultFeature('Anti-Corrosion'), defaultFeature('Anti-Aging')],
        button: {
          show: false,
          backgroundColor: '#FF8100',
          color: '#FFFFFF',
          radius: 4,
          text: 'BUY NOW',
          link: '#',
          iconPrefix: { show: false, src: '' },
          iconSuffix: { show: false, src: '' },
          subBtn: { show: false, text: '' },
        },
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(value) {
        if (value?.sectionExtract) this.applyExtract(value.sectionExtract);
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        header: { ...this.fields.header, ...(extract.header || {}) },
        media: { ...this.fields.media, ...(extract.media || {}) },
        features: Array.isArray(extract.features) ? extract.features : this.fields.features,
        button: {
          ...this.fields.button,
          ...(extract.button || {}),
          iconPrefix: { ...this.fields.button.iconPrefix, ...((extract.button || {}).iconPrefix || {}) },
          iconSuffix: { ...this.fields.button.iconSuffix, ...((extract.button || {}).iconSuffix || {}) },
          subBtn: { ...this.fields.button.subBtn, ...((extract.button || {}).subBtn || {}) },
        },
      };
    },
    generateObjectID() {
      const hex = value => Math.floor(value).toString(16);
      return hex(Date.now() / 1000) + ' '.repeat(16).replace(/./g, () => hex(Math.random() * 16));
    },
    handleUploadDecor(obj) {
      this.fields.header.decor = obj?.imageSrc || '';
    },
    handleRemoveDecor() {
      this.fields.header.decor = '';
    },
    handleUploadMediaImage(obj) {
      this.fields.media.imageSrc = obj?.imageSrc || '';
    },
    handleRemoveMediaImage() {
      this.fields.media.imageSrc = '';
    },
    handleAddFeature() {
      this.fields.features.push(defaultFeature('Feature'));
    },
    handleRemoveFeature(index) {
      this.fields.features.splice(index, 1);
    },
    handleUploadFeatureIcon(obj, index) {
      if (this.fields.features[index]) this.$set(this.fields.features[index], 'icon', obj?.imageSrc || '');
    },
    handleRemoveFeatureIcon(index) {
      if (this.fields.features[index]) this.$set(this.fields.features[index], 'icon', '');
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const html = updateHtmlFromData.sectionFurnitureshopBanner03(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', { ...this.sectionEditor.section, title: this.fields.title, html });
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.editor-content {
  max-height: 70vh;
  overflow-y: auto;
}
.group-item {
  padding: 15px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
}
.media-preview {
  display: block;
  width: 100%;
  max-height: 260px;
  margin-top: 16px;
  background: #000;
}
::v-deep .ck-editor__editable_inline {
  min-height: 100px;
}
</style>
